import React, { useState } from "react";
import {
    Box,
    Typography,
    IconButton,
    TextField,
    Card,
    CardContent,
    Stack,
    Grid,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { useTranslation } from "@/hooks/useTranslation";
import LazyImage from "@/theme/partials/lazyimage";
import Icon from "@/theme/vectors/bundle";
import { formatDuration } from "@/theme/utils";
import { PLACEHOLDER_IMAGES } from "@/utils/web/constants";

const CartItem = ({ item, onUpdateQuantity, onRemove }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [quantity, setQuantity] = useState(item.quantity);

    // Format giá tiền
    const formatPrice = (price) => {
        return new Intl.NumberFormat("vi-VN", {
            style: "currency",
            currency: "VND",
        }).format(price);
    };

    // Xử lý thay đổi số lượng
    const handleQuantityChange = (e) => {
        const newValue = parseInt(e.target.value, 10);
        if (isNaN(newValue) || newValue < 1) return;

        setQuantity(newValue);
        onUpdateQuantity(item.variantId, newValue);
    };

    // Tăng số lượng
    const increaseQuantity = () => {
        const newValue = quantity + 1;
        setQuantity(newValue);
        onUpdateQuantity(item.variantId, newValue);
    };

    // Giảm số lượng
    const decreaseQuantity = () => {
        if (quantity <= 1) return;
        const newValue = quantity - 1;
        setQuantity(newValue);
        onUpdateQuantity(item.variantId, newValue);
    };

    return (
        <Card sx={{ mb: 2, overflow: "visible" }}>
            <CardContent>
                <Grid container spacing={2}>
                    {/* Hình ảnh sản phẩm */}
                    <Grid item xs={4} sm={3} md={2}>
                        <LazyImage
                            src={item.mediaUrl || PLACEHOLDER_IMAGES.PRODUCT}
                            alt={item.productName}
                            height="100%"
                            sx={{
                                width: "100%",
                                aspectRatio: "1/1",
                                objectFit: "cover",
                                borderRadius: 1,
                            }}
                        />
                    </Grid>

                    {/* Thông tin sản phẩm */}
                    <Grid item xs={8} sm={9} md={10}>
                        <Grid container spacing={2}>
                            {/* Tên và thuộc tính sản phẩm */}
                            <Grid item xs={12} md={6}>
                                <Stack spacing={1}>
                                    <Typography variant="h6" component="div">
                                        {item.productName}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        {item.attributeSummary}
                                    </Typography>
                                </Stack>
                            </Grid>

                            {/* Giá và số lượng */}
                            <Grid item xs={12} md={6}>
                                <Stack
                                    direction={isMobile ? "column" : "row"}
                                    spacing={2}
                                    justifyContent="space-between"
                                    alignItems={
                                        isMobile ? "flex-start" : "center"
                                    }
                                    height="100%"
                                >
                                    {/* Giá tiền */}
                                    <Typography
                                        variant="subtitle1"
                                        color="primary"
                                        fontWeight="bold"
                                    >
                                        {formatPrice(item.price)}
                                    </Typography>

                                    {/* Số lượng */}
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                        }}
                                    >
                                        <IconButton
                                            size="small"
                                            onClick={decreaseQuantity}
                                            disabled={quantity <= 1}
                                        >
                                            <Icon name="delete" size={16} />
                                        </IconButton>
                                        <TextField
                                            size="small"
                                            value={quantity}
                                            onChange={handleQuantityChange}
                                            inputProps={{
                                                min: 1,
                                                style: {
                                                    textAlign: "center",
                                                    width: "40px",
                                                },
                                            }}
                                            sx={{ mx: 1 }}
                                        />
                                        <IconButton
                                            size="small"
                                            onClick={increaseQuantity}
                                        >
                                            <Icon name="add" size={16} />
                                        </IconButton>
                                    </Box>

                                    {/* Tổng tiền và nút xóa */}
                                    <Stack
                                        direction="row"
                                        spacing={2}
                                        alignItems="center"
                                    >
                                        <Typography
                                            variant="subtitle1"
                                            fontWeight="medium"
                                        >
                                            {formatPrice(item.price * quantity)}
                                        </Typography>
                                        <IconButton
                                            color="error"
                                            size="small"
                                            onClick={() =>
                                                onRemove(item.variantId)
                                            }
                                        >
                                            <Icon name="remove" size={16} />
                                        </IconButton>
                                    </Stack>
                                </Stack>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
};

export default CartItem;
